import { CheckItem, CheckRecord, CheckSummary, HazardLevel, CheckType } from '@/types'
import { generateOverviewWithLLM } from '@/services/llm'
import { getHazardItems } from '@/data/hazardItems'

// 生成检查总结
export async function generateCheckSummary(record: CheckRecord): Promise<CheckSummary> {
  // 分类隐患项目（含违反依据）
  const majorHazards = record.selectedItems
    .filter(item => item.hazardLevel === HazardLevel.MAJOR)
    .map(item => item.regulatoryBasis ? `${item.name}（违反：${item.regulatoryBasis}）` : item.name)

  const generalHazards = record.selectedItems
    .filter(item => item.hazardLevel === HazardLevel.GENERAL)
    .map(item => item.regulatoryBasis ? `${item.name}（违反：${item.regulatoryBasis}）` : item.name)

  const builtInCount = record.selectedItems.filter(item => !item.isCustom).length
  const customCount = record.selectedItems.filter(item => item.isCustom).length

  // 共检查重点项目数量 = 该检查类型的内置隐患项目数据总数
  const inspectedItemCount = getHazardItems(record.checkType).length

  // 生成检查概况（优先使用大模型，失败则回退到模板）
  const overview = await generateOverview(
    record,
    majorHazards.length,
    generalHazards.length,
    builtInCount,
    customCount,
    inspectedItemCount
  )

  // 生成整改建议
  const suggestions = generateSuggestions(record.selectedItems)

  return {
    overview,
    majorHazards,
    generalHazards,
    suggestions,
    generatedAt: new Date(),
  }
}

// 生成检查概况（大模型优先，失败回退模板）
async function generateOverview(
  record: CheckRecord,
  majorCount: number,
  generalCount: number,
  builtInCount: number,
  customCount: number,
  inspectedItemCount: number
): Promise<string> {
  const checkTypeText = record.checkType === CheckType.LIQUEFIED_GAS ? '瓶装液化石油气' : '管道天然气'

  // 提取隐患名称（不含违反依据后缀，用于 LLM 理解）
  const majorHazardNames = record.selectedItems
    .filter(item => item.hazardLevel === HazardLevel.MAJOR)
    .map(item => item.name)

  const generalHazardNames = record.selectedItems
    .filter(item => item.hazardLevel === HazardLevel.GENERAL)
    .map(item => item.name)

  try {
    const result = await generateOverviewWithLLM({
      checkType: checkTypeText,
      inspectedUnit: record.inspectedUnit,
      inspectedAddress: record.inspectedAddress,
      checkDate: formatDate(record.checkDate),
      totalItems: inspectedItemCount,
      majorHazards: majorHazardNames,
      generalHazards: generalHazardNames,
    })

    if (result) return result
  } catch {
    // LLM 调用失败，回退到模板生成
  }

  // 模板回退
  const totalItems = majorCount + generalCount
  const customSuffix = customCount > 0 ? `（其中内置隐患${builtInCount}项，自定义隐患${customCount}项）` : ''

  let riskText = '安全状况良好'
  if (majorCount > 0) {
    riskText = '存在较大安全风险'
  } else if (generalCount > 0) {
    riskText = '存在一定安全隐患'
  }

  return `${formatDate(record.checkDate)}，对${record.inspectedUnit}进行${checkTypeText}安全检查，共检查${inspectedItemCount}个重点项目，发现隐患${totalItems}项${customSuffix}，其中重大隐患${majorCount}项，一般隐患${generalCount}项。整体${riskText}。`
}

// 根据隐患项目匹配对应的整改建议（无匹配时返回 null）
function matchSuggestion(item: CheckItem): string | null {
  if (item.name.includes('熄火保护')) {
    return '更换带有熄火保护装置的合格灶具'
  } else if (item.name.includes('气化器')) {
    return '拆除用气房间内的气化器，气化器应设置在专用气化间'
  } else if (item.name.includes('公共用餐区域')) {
    return '将气瓶移出公共用餐区域，放置于专用瓶组间'
  } else if (item.name.includes('大中型商店')) {
    return '大中型商店建筑内禁止使用液化石油气气瓶，改用管道天然气'
  } else if (item.name.includes('加热')) {
    return '严禁对气瓶进行加热，应采取自然气化方式'
  } else if (item.name.includes('横卧') || item.name.includes('倒卧')) {
    return '气瓶必须直立使用，严禁横卧或倒卧'
  } else if (item.name.includes('调压器')) {
    return '更换为符合GB35844-2018标准的不可调节式调压器'
  } else if (item.name.includes('橡胶软管')) {
    return '更换为专用燃气连接软管，使用年限不低于燃具判废年限'
  } else if (item.name.includes('接口') || item.name.includes('三通')) {
    return '拆除软管中间的三通或接口，软管应直接连接灶具与阀门'
  } else if (item.name.includes('漏气')) {
    return '立即停气并委托有资质的单位修复漏气管道，修复后进行气密性试验'
  } else if (item.name.includes('封堵')) {
    return '对室内燃气管道末端进行有效封堵，确保密封可靠'
  } else if (item.name.includes('擅自安装') || item.name.includes('改装') || item.name.includes('拆除')) {
    return '立即停止擅自改动行为，委托有资质的单位恢复原状，并依法承担相关费用'
  } else if (item.name.includes('两种') && item.name.includes('燃料')) {
    return '立即移除其他燃料（油、煤、生物质等），同一用气场所严禁同时使用两种及以上燃料'
  } else if (item.name.includes('切断阀')) {
    return '安装与燃气浓度检测报警器连锁的紧急切断阀，确保报警时自动切断气源'
  } else if (item.name.includes('通风') || item.name.includes('维护操作')) {
    return '整改用气场所，确保通风良好、符合安全使用条件且便于维护操作'
  } else if (item.name.includes('阀门') || item.name.includes('启闭标记')) {
    return '在燃气进口和燃具前管道上安装独立阀门，并做明显启闭标记'
  } else if (item.name.includes('报警器')) {
    return '按规范安装燃气泄漏报警器，安装间距、安装高度应符合《城镇燃气报警控制系统技术规程》要求'
  }
  return null
}

// 生成整改建议：每个隐患项目均生成对应建议，无预设匹配时回退到通用建议
function generateSuggestions(items: CheckItem[]): string[] {
  const suggestions: string[] = []
  const seen = new Set<string>()

  items.forEach(item => {
    const matched = matchSuggestion(item)
    const suggestion =
      matched ??
      (item.regulatoryBasis
        ? `针对"${item.name}"，应限期整改，消除安全隐患（违反：${item.regulatoryBasis}）`
        : `针对"${item.name}"，应限期整改，消除安全隐患`)

    if (!seen.has(suggestion)) {
      seen.add(suggestion)
      suggestions.push(suggestion)
    }
  })

  return suggestions
}

// 格式化日期
function formatDate(date: Date): string {
  const d = new Date(date)
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}
